import React from 'react';
import { useTranslation } from 'react-i18next';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { FieldPanel, FieldPanelHeader } from 'styles/v2/Styles.styled';
import { ConversionFactorModel, EPDBasedOnUnitEnum, Option, ReferenceFlowModel } from 'types/types';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import { useDictionaryValues, useEpdConversionFactor, useEpdReferenceFlow } from 'services/api/queries';
import {
  useUpdateEpdGeneralInformation,
  useUpsertEpdConversionFactor,
  useUpsertEpdReferenceFlow,
} from 'services/api/mutations';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import WizardNumberInput from 'components/v2/epd-wizard-inputs/WizardNumberInput';

const ReferenceFlowSection: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  epdBasedOnUnit?: string;
  errors?: any;
}> = ({ epdId, epdVersionId, epdBasedOnUnit, errors }) => {
  const { t } = useTranslation();
  const referenceFlowData = useEpdReferenceFlow(epdVersionId!).data;
  const upsertEpdReferenceFlow = useUpsertEpdReferenceFlow(epdVersionId);
  const conversionFactorData = useEpdConversionFactor(epdVersionId!).data;
  const upsertEpdConversionFactor = useUpsertEpdConversionFactor(epdVersionId);
  const updateEpdGeneralInformation = useUpdateEpdGeneralInformation(epdVersionId);

  const dictionariesValues = useDictionaryValues().data!;
  const referenceFlowUnits =
    dictionariesValues.filter((x) => x.dictionaryCode === EPD_DICTIONARY_CODES.REFERENCE_FLOW_UNIT) || [];
  const unitOptions: Option[] = referenceFlowUnits.map((x) => ({
    value: x.value.toLowerCase(),
    label: x.description,
  }));

  const selectedReferenceFlowUnit = unitOptions.find((x) => x.value === referenceFlowData?.unitId);

  const updateDependentFieldsIfNeeded = (newValue: ReferenceFlowModel) => {
    const currentUnitIsMass = unitOptions.find((x) => x.value === newValue.unitId)?.label === 'Mass (kg)';
    const previousUnitIsMass = unitOptions.find((x) => x.value === referenceFlowData?.unitId)?.label === 'Mass (kg)';
    if (currentUnitIsMass && !previousUnitIsMass) {
      upsertEpdConversionFactor.mutate({ ...conversionFactorData, value: 1 } as ConversionFactorModel);
    } else if (!currentUnitIsMass && previousUnitIsMass) {
      upsertEpdConversionFactor.mutate({ ...conversionFactorData, value: undefined } as ConversionFactorModel);
    }

    if (currentUnitIsMass) {
      updateEpdGeneralInformation.mutate({
        epdId,
        versionId: epdVersionId,
        propertyName: 'massOfDeclaredUnit',
        newValue: newValue?.value,
      });
    }
  };

  const onChangeReferenceFlow = async (propertyName: string, val: any) => {
    const newValue = { ...referenceFlowData, [propertyName]: val } as ReferenceFlowModel;
    updateDependentFieldsIfNeeded(newValue);
    upsertEpdReferenceFlow.mutate(newValue);
  };

  return (
    <FieldPanel>
      <FieldPanelHeader className="w-full">
        {t(
          `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.title.${
            epdBasedOnUnit ?? Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
          }`
        )}
        <TooltipHelpIcon
          content={t(
            `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.tooltip.${
              epdBasedOnUnit ?? Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
            }`
          )}
        />
      </FieldPanelHeader>
      <WizardTextInputComponent
        containerClassName="w-full"
        tooltip="Example: Cement (CEM II 32.5). Unit: Mass (kg). Value: 1.0"
        label={t(`epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.name`)}
        required
        value={referenceFlowData?.productFlowName}
        onChanged={onChangeReferenceFlow}
        name="productFlowName"
        maxLength={150}
        error={errors?.productFlowName}
        placeholder="Type here"
      />
      <CompilerSelectControlledComponent
        label={t(`epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.unit`)}
        name="unitId"
        options={unitOptions}
        required
        value={selectedReferenceFlowUnit}
        onChanged={onChangeReferenceFlow}
        placeholder="Select"
        isClearable={false}
        error={errors?.productFlowUnit}
        {...reactSelectPropsForHtmlLabel}
      />
      <WizardNumberInput
        label={t(`epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.value`)}
        name="value"
        required
        placeholder="Type number"
        defaultValue={referenceFlowData?.value ?? null}
        onChanged={onChangeReferenceFlow}
        error={errors?.productFlowValue}
        maxFractionDigits={5}
      />
    </FieldPanel>
  );
};

export default ReferenceFlowSection;
